// Platos Page with complete ingredient management
import React, { useEffect, useState } from 'react';
import { platosAPI, ingredientesAPI, alergenosAPI, materialesAPI, getAuthToken } from '../api/api';
import { Plus, Search, Edit, Trash2, Clock, X, ChefHat, DollarSign, FileText, Loader2 } from 'lucide-react';
import AlergenoIcon, { AlergenoList } from '../components/AlergenoIcon';
import LoadingOverlay from '../components/LoadingOverlay';

// Función para normalizar texto (quitar acentos) para búsquedas
const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const Platos = () => {
  const [platos, setPlatos] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [alergenos, setAlergenos] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [ingredienteSearch, setIngredienteSearch] = useState('');
  const [showIngredienteDropdown, setShowIngredienteDropdown] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [materialEditIndex, setMaterialEditIndex] = useState(null);
  const [bandejaSearch, setBandejaSearch] = useState('');
  const [showBandejaDropdown, setShowBandejaDropdown] = useState(false);
  const [filtroActivo, setFiltroActivo] = useState('true');
  const [filtroTipo, setFiltroTipo] = useState('plato'); // 'plato' o 'servicio'
  const [showCreateMaterialModal, setShowCreateMaterialModal] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newMaterialTipo, setNewMaterialTipo] = useState('desechable');
  const [showModal, setShowModal] = useState(false);
  const [editingPlato, setEditingPlato] = useState(null);
  const [activeTab, setActiveTab] = useState('datos'); // 'datos' o 'tareas'
  const [tareasPlato, setTareasPlato] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [showTareaModal, setShowTareaModal] = useState(false);
  const [translating, setTranslating] = useState(false);  // Estado para traducción IA
  const [showDesactivarModal, setShowDesactivarModal] = useState(false);
  const [desactivarPlatoId, setDesactivarPlatoId] = useState(null);
  const [desactivarPlatoNombre, setDesactivarPlatoNombre] = useState('');
  const [menusAfectados, setMenusAfectados] = useState([]);
  const [desactivarAccion, setDesactivarAccion] = useState('eliminar');
  const [platoSustitutoId, setPlatoSustitutoId] = useState('');
  const [sustitutoSearch, setSustitutoSearch] = useState('');
  const [procesandoDesactivar, setProcesandoDesactivar] = useState(false);
  const [tareaFormData, setTareaFormData] = useState({
    nombre_tarea: '',
    fase: '0',
    lote_unidades: 10,
    tiempo_por_lote_min: 15,
    tiempo_activo_min: 0,
    tiempo_pasivo_min: 0,
    tipo_tiempo_pasivo: 'proporcional',
    capacidad_recurso: '',
    nombre_recurso: '',
    responsable_sugerido: 'Cocina',
    observaciones: ''
  });
  const [formData, setFormData] = useState({
    nombre: '',
    nombre_en: '',  // Nombre en inglés para minutas bilingües
    tipo: 'plato',  // 'plato' o 'servicio'
    categoria: '',
    porciones: 1,  // Para cuántas personas es esta receta
    elaboracion_entera: false,  // Redondear hacia arriba las elaboraciones (material fijo)
    tiempo_preparacion_min: 0,
    tiempo_activo_min: 0,
    tiempo_pasivo_min: 0,
    precio_sugerido: 0,
    ingredientes: {},
    material_elaboracion: [],
    instrucciones_elaboracion: '',
    formato_presentacion: '',
    unidades_por_persona: 1.0,
    activo: true,
    oculto_en_documentos: false,  // LEGACY - para compatibilidad
    oculto_en_presupuestos: false,  // Para ocultar en presupuestos PDF
    oculto_en_minutas: false,  // Para ocultar en minutas
    // Configuración de bandeja
    tiene_bandeja: false,
    bandeja_material_id: '',
    bandeja_nombre: '',
    uds_por_bandeja: 20
  });

  useEffect(() => {
    fetchData();
    
    // Cerrar dropdown al hacer click fuera
    const handleClickOutside = (event) => {
      if (showIngredienteDropdown && !event.target.closest('.ingredient-autocomplete')) {
        setShowIngredienteDropdown(false);
      }
      if (showMaterialDropdown && !event.target.closest('.material-autocomplete')) {
        setShowMaterialDropdown(false);
        setMaterialEditIndex(null);
        setMaterialSearch('');
      }
      if (showBandejaDropdown && !event.target.closest('.bandeja-autocomplete')) {
        setShowBandejaDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showIngredienteDropdown, showMaterialDropdown, showBandejaDropdown]);

  const fetchData = async () => {
    try {
      const [platosRes, ingredientesRes, alergenosRes, materialesRes] = await Promise.all([
        platosAPI.getAll(),
        ingredientesAPI.getAll(),
        alergenosAPI.getAll(),
        materialesAPI.getAll()
      ]);
      setPlatos(Array.isArray(platosRes.data) ? platosRes.data : []);
      setIngredientes(Array.isArray(ingredientesRes.data) ? ingredientesRes.data : []);
      setAlergenos(Array.isArray(alergenosRes.data) ? alergenosRes.data : []);
      setMateriales(Array.isArray(materialesRes.data) ? materialesRes.data.filter(m => m.activo) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este plato?')) {
      try {
        await platosAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting plato:', error);
      }
    }
  };

  const openModal = (plato = null) => {
    setActiveTab('datos'); // Reset to datos tab
    setTareasPlato([]); // Clear tareas
    
    if (plato) {
      setEditingPlato(plato);
      setFormData({
        nombre: plato.nombre,
        nombre_en: plato.nombre_en || '',
        tipo: plato.tipo || 'plato',
        categoria: plato.categoria || '',
        porciones: plato.porciones || 1,
        elaboracion_entera: plato.elaboracion_entera || false,
        tiempo_preparacion_min: plato.tiempo_preparacion_min || 0,
        tiempo_activo_min: plato.tiempo_activo_min ?? 0,
        tiempo_pasivo_min: plato.tiempo_pasivo_min ?? 0,
        precio_sugerido: plato.precio_sugerido,
        ingredientes: plato.ingredientes || {},
        material_elaboracion: plato.material_elaboracion || [],
        instrucciones_elaboracion: plato.instrucciones_elaboracion || '',
        formato_presentacion: plato.formato_presentacion || '',
        unidades_por_persona: plato.unidades_por_persona || 1.0,
        activo: plato.activo,
        oculto_en_documentos: plato.oculto_en_documentos || false,
        // Nuevos campos separados - si el legacy está activo, activar ambos
        oculto_en_presupuestos: plato.oculto_en_presupuestos ?? plato.oculto_en_documentos ?? false,
        oculto_en_minutas: plato.oculto_en_minutas ?? plato.oculto_en_documentos ?? false,
        // Configuración de bandeja
        tiene_bandeja: plato.tiene_bandeja || false,
        bandeja_material_id: plato.bandeja_material_id || '',
        bandeja_nombre: plato.bandeja_nombre || '',
        uds_por_bandeja: plato.uds_por_bandeja || 20
      });
      // Load tareas for this plato
      fetchTareasPlato(plato.id);
    } else {
      setEditingPlato(null);
      setFormData({
        nombre: '',
        nombre_en: '',
        tipo: 'plato',
        categoria: '',
        porciones: 1,
        elaboracion_entera: false,
        tiempo_preparacion_min: 0,
        tiempo_activo_min: 0,
        tiempo_pasivo_min: 0,
        precio_sugerido: 0,
        ingredientes: {},
        material_elaboracion: [],
        instrucciones_elaboracion: '',
        formato_presentacion: '',
        unidades_por_persona: 1.0,
        activo: true,
        oculto_en_documentos: false,
        oculto_en_presupuestos: false,
        oculto_en_minutas: false,
        // Configuración de bandeja
        tiene_bandeja: false,
        bandeja_material_id: '',
        bandeja_nombre: '',
        uds_por_bandeja: 20
      });
    }
    setShowModal(true);
  };


  // Funciones para gestionar tareas del plato
  const fetchTareasPlato = async (platoId) => {
    setLoadingTareas(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tareas-plato?plato_id=${platoId}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const data = await response.json();
      setTareasPlato(data.tareas || []);
    } catch (error) {
      console.error('Error fetching tareas:', error);
    } finally {
      setLoadingTareas(false);
    }
  };

  const openTareaModal = (tarea = null) => {
    if (tarea) {
      setEditingTarea(tarea);
      setTareaFormData({
        nombre_tarea: tarea.nombre_tarea,
        fase: tarea.fase,
        lote_unidades: tarea.lote_unidades,
        tiempo_por_lote_min: tarea.tiempo_por_lote_min,
        tiempo_activo_min: tarea.tiempo_activo_min || 0,
        tiempo_pasivo_min: tarea.tiempo_pasivo_min || 0,
        tipo_tiempo_pasivo: tarea.tipo_tiempo_pasivo || 'proporcional',
        capacidad_recurso: tarea.capacidad_recurso || '',
        nombre_recurso: tarea.nombre_recurso || '',
        responsable_sugerido: tarea.responsable_sugerido,
        observaciones: tarea.observaciones || ''
      });
    } else {
      setEditingTarea(null);
      setTareaFormData({
        nombre_tarea: '',
        fase: '0',
        lote_unidades: 10,
        tiempo_por_lote_min: 15,
        tiempo_activo_min: 0,
        tiempo_pasivo_min: 0,
        tipo_tiempo_pasivo: 'proporcional',
        capacidad_recurso: '',
        nombre_recurso: '',
        responsable_sugerido: 'Cocina',
        observaciones: ''
      });
    }
    setShowTareaModal(true);
  };

  const closeTareaModal = () => {
    setShowTareaModal(false);
    setEditingTarea(null);
  };

  const handleTareaSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingPlato) {
      alert('Primero debes guardar el plato antes de añadir tareas');
      return;
    }

    try {
      const tareaData = {
        ...tareaFormData,
        plato_id: editingPlato.id
      };

      if (editingTarea) {
        // Update existing tarea
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tareas-plato/${editingTarea.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify(tareaFormData)
        });
      } else {
        // Create new tarea
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tareas-plato`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify(tareaData)
        });
      }

      closeTareaModal();
      fetchTareasPlato(editingPlato.id);
    } catch (error) {
      console.error('Error saving tarea:', error);
      alert('Error al guardar la tarea');
    }
  };

  const handleDeleteTarea = async (tareaId) => {
    if (window.confirm('¿Estás seguro de eliminar esta tarea?')) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tareas-plato/${tareaId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
        fetchTareasPlato(editingPlato.id);
      } catch (error) {
        console.error('Error deleting tarea:', error);
      }
    }
  };

  // Toggle revisado status
  const handleToggleRevisado = async (platoId, currentRevisado) => {
    try {
      // Usar PATCH para actualización parcial
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platos/${platoId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ revisado: !currentRevisado })
      });
      
      if (response.ok) {
        // Actualizar el estado local
        setPlatos(prev => prev.map(p => 
          p.id === platoId ? { ...p, revisado: !currentRevisado } : p
        ));
      }
    } catch (error) {
      console.error('Error updating revisado:', error);
    }
  };

  // Toggle oculto en documentos (minutas y presupuestos)
  const handleToggleOcultoDocumentos = async (platoId, currentOculto) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platos/${platoId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ oculto_en_documentos: !currentOculto })
      });
      
      if (response.ok) {
        setPlatos(prev => prev.map(p => 
          p.id === platoId ? { ...p, oculto_en_documentos: !currentOculto } : p
        ));
      }
    } catch (error) {
      console.error('Error updating oculto_en_documentos:', error);
    }
  };


  const closeModal = () => {
    setShowModal(false);
    setEditingPlato(null);
    setFormData({
      nombre: '',
      nombre_en: '',
      tipo: 'plato',
      categoria: '',
      porciones: 1,
      elaboracion_entera: false,
      tiempo_preparacion_min: 0,
      tiempo_activo_min: 0,
      tiempo_pasivo_min: 0,
      precio_sugerido: 0,
      ingredientes: {},
      material_elaboracion: [],
      instrucciones_elaboracion: '',
      formato_presentacion: '',
      unidades_por_persona: 1.0,
      activo: true,
      oculto_en_documentos: false,
      tiene_bandeja: false,
      bandeja_material_id: '',
      bandeja_nombre: '',
      uds_por_bandeja: 20
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Interceptar desactivación: si el plato estaba activo y ahora se desactiva
    if (editingPlato && editingPlato.activo && !formData.activo) {
      try {
        const res = await platosAPI.getMenusAfectados(editingPlato.id);
        const menus = res.data.menus || [];
        if (menus.length > 0) {
          setDesactivarPlatoId(editingPlato.id);
          setDesactivarPlatoNombre(editingPlato.nombre);
          setMenusAfectados(menus);
          setDesactivarAccion('eliminar');
          setPlatoSustitutoId('');
          setSustitutoSearch('');
          setShowDesactivarModal(true);
          return; // No guardar aún, esperar acción del usuario
        }
      } catch (err) {
        console.error('Error checking menus:', err);
        // Si falla la comprobación, continuar con guardado normal
      }
    }
    
    await guardarPlato();
  };
  
  const guardarPlato = async () => {
    setSaving(true);
    try {
      // Calcular el coste total antes de guardar
      const costBreakdown = calculateCostBreakdown();
      const dataToSave = {
        ...formData,
        coste_total: costBreakdown.total,
        coste: costBreakdown.total / (parseInt(formData.porciones) || 1), // Coste por porción
        coste_ingredientes: costBreakdown.ingredientes,
        coste_materiales: costBreakdown.materiales,
        coste_personal: costBreakdown.personal
      };
      
      if (editingPlato) {
        const response = await platosAPI.update(editingPlato.id, dataToSave);
      } else {
        const response = await platosAPI.create(dataToSave);
      }
      
      await fetchData();
      closeModal();
    } catch (error) {
      console.error('❌ Error saving plato:', error);
      console.error('❌ Error details:', error.response?.data);
      let errorMsg = 'Error desconocido';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as an array
        errorMsg = detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n');
      } else if (error.message) {
        errorMsg = error.message;
      }
      alert(`Error al guardar el plato:\n${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDesactivarConAccion = async () => {
    setProcesandoDesactivar(true);
    try {
      // 1. Ejecutar acción en menús
      await platosAPI.desactivar(desactivarPlatoId, {
        accion: desactivarAccion,
        plato_sustituto_id: desactivarAccion === 'sustituir' ? platoSustitutoId : null
      });
      
      // 2. Guardar el plato (ya con activo=false)
      await guardarPlato();
      
      setShowDesactivarModal(false);
      setDesactivarPlatoId(null);
    } catch (error) {
      console.error('Error desactivando plato:', error);
      alert(`Error al procesar la desactivación: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcesandoDesactivar(false);
    }
  };

  const addIngrediente = (ingredienteId) => {
    setFormData({
      ...formData,
      ingredientes: {
        ...formData.ingredientes,
        [ingredienteId]: 1.0
      }
    });
  };

  const updateIngredienteCantidad = (ingredienteId, cantidad) => {
    setFormData({
      ...formData,
      ingredientes: {
        ...formData.ingredientes,
        [ingredienteId]: parseFloat(cantidad) || 0
      }
    });
  };

  const removeIngrediente = (ingredienteId) => {
    const newIngredientes = { ...formData.ingredientes };
    delete newIngredientes[ingredienteId];
    setFormData({
      ...formData,
      ingredientes: newIngredientes
    });
  };

  // Calculate estimated cost - DESGLOSADO
  const calculateCostBreakdown = () => {
    const COSTE_PERSONAL_POR_MINUTO = 0.25; // 15€/hora
    
    // 1. Coste de ingredientes
    let coste_ingredientes = 0;
    Object.entries(formData.ingredientes).forEach(([id, cantidad]) => {
      const ingrediente = ingredientes.find(i => i.id === id);
      if (ingrediente) {
        coste_ingredientes += ingrediente.precio_unitario * cantidad;
      }
    });
    
    // 2. Coste de materiales
    let coste_materiales = 0;
    formData.material_elaboracion.forEach((material) => {
      const materialData = materiales.find(m => m.id === material.material_id);
      if (materialData && material.cantidad && !isNaN(material.cantidad)) {
        const precio = materialData.precio_unitario || 0;
        const cantidad = parseFloat(material.cantidad) || 0;
        coste_materiales += precio * cantidad;
      }
    });
    
    // 3. Coste de personal (solo tiempo ACTIVO - el pasivo no requiere mano de obra)
    const tiempo_activo = parseInt(formData.tiempo_activo_min) || 0;
    const coste_personal = tiempo_activo * COSTE_PERSONAL_POR_MINUTO;
    
    const total = coste_ingredientes + coste_materiales + coste_personal;
    
    return {
      ingredientes: coste_ingredientes,
      materiales: coste_materiales,
      personal: coste_personal,
      total: total
    };
  };

  const calculateEstimatedCost = () => {
    return calculateCostBreakdown().total;
  };

  // Función para normalizar texto (sin tildes)
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  // Filtrar ingredientes por búsqueda (sin importar tildes) — solo activos para añadir nuevos
  const filteredIngredientes = ingredientes.filter(ing => {
    if (!ing.activo) return false;
    if (!ingredienteSearch) return true;
    const searchNormalized = normalizeText(ingredienteSearch);
    const nombreNormalized = normalizeText(ing.nombre);
    return nombreNormalized.includes(searchNormalized) && !formData.ingredientes[ing.id];
  });

  const reactivarIngrediente = async (ingrediente) => {
    if (!ingrediente || !ingrediente.id) return;
    if (!window.confirm(`¿Reactivar "${ingrediente.nombre}"? Volverá a estar disponible en todo el catálogo.`)) return;
    try {
      const payload = { ...ingrediente, activo: true };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      await ingredientesAPI.update(ingrediente.id, payload);
      setIngredientes(prev => prev.map(i => i.id === ingrediente.id ? { ...i, activo: true } : i));
    } catch (err) {
      console.error('Error reactivando ingrediente:', err);
      alert('No se pudo reactivar el ingrediente. Revisa la consola.');
    }
  };

  // Filtrar materiales por búsqueda (sin importar tildes)
  const filteredMateriales = materiales.filter(mat => {
    if (!materialSearch) return true;
    const searchNormalized = normalizeText(materialSearch);
    const nombreNormalized = normalizeText(mat.nombre);
    return nombreNormalized.includes(searchNormalized);
  });


  // Material management functions
  const addMaterial = () => {
    setFormData({
      ...formData,
      material_elaboracion: [
        ...formData.material_elaboracion,
        { 
          nombre: '', 
          cantidad: 1, 
          unidad: 'ud', 
          tipo: 'elaboracion',
          tipo_uso: 'por_unidades',  // Por defecto "por unidades"
          unidades_plato: 1  // Por defecto 1 ración
        }
      ]
    });
  };

  const updateMaterial = (index, field, value) => {
    const newMateriales = [...formData.material_elaboracion];
    newMateriales[index][field] = value;
    setFormData({
      ...formData,
      material_elaboracion: newMateriales
    });
  };

  const removeMaterial = (index) => {
    const newMateriales = formData.material_elaboracion.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      material_elaboracion: newMateriales
    });
  };

  const handleCreateQuickMaterial = async () => {
    if (!newMaterialName.trim()) {
      alert('Por favor ingresa un nombre para el material');
      return;
    }
    
    try {
      const response = await materialesAPI.create({
        nombre: newMaterialName,
        tipo: newMaterialTipo,
        stock: 0,
        activo: true
      });
      
      // Agregar el nuevo material a la lista
      setMateriales([...materiales, response.data]);
      
      // Cerrar modal y limpiar
      setShowCreateMaterialModal(false);
      setNewMaterialName('');
      setNewMaterialTipo('desechable');
      
      alert('Material creado exitosamente');
    } catch (error) {
      console.error('Error creating material:', error);
      alert('Error al crear material');
    }
  };


  // Get inherited allergens
  const getInheritedAlergenos = () => {
    const alergenosSet = new Set();
    Object.keys(formData.ingredientes).forEach(ingredienteId => {
      const ingrediente = ingredientes.find(i => i.id === ingredienteId);
      if (ingrediente && ingrediente.alergenos_ids) {
        ingrediente.alergenos_ids.forEach(alergenoId => alergenosSet.add(alergenoId));
      }
    });
    return Array.from(alergenosSet);
  };

  const filteredPlatos = platos.filter(plato => {
    const matchesSearch = normalizeText(plato.nombre).includes(normalizeText(searchTerm));
    const matchesTipo = plato.tipo === filtroTipo;
    const matchesActivo = filtroActivo === 'todos' || 
                          (filtroActivo === 'true' && plato.activo) || 
                          (filtroActivo === 'false' && !plato.activo);
    return matchesSearch && matchesTipo && matchesActivo;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      {/* Overlay de guardado */}
      <LoadingOverlay 
        isLoading={saving} 
        message={editingPlato ? "Actualizando plato..." : "Creando plato..."} 
      />
      
      {/* Header responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="platos-title">
            {filtroTipo === 'plato' ? 'Platos' : 'Servicios'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {filtroTipo === 'plato' ? 'Catálogo de platos y recetas' : 'Servicios y complementos para eventos'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              const xhr = new XMLHttpRequest();
              xhr.open('GET', `${process.env.REACT_APP_BACKEND_URL}/api/platos/generar-listado-pdf`, true);
              xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
              xhr.responseType = 'blob';
              
              xhr.onload = function() {
                if (xhr.status === 200) {
                  const blob = xhr.response;
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Listado_Platos_${new Date().toISOString().split('T')[0]}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } else {
                  alert('Error al generar el PDF del listado');
                }
              };
              
              xhr.onerror = function() {
                alert('Error al generar el PDF del listado');
              };
              
              xhr.send();
            }}
            className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 hover:bg-green-700 transition-colors text-sm sm:text-base"
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Descargar Listado PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button
            onClick={() => openModal()}
            data-testid="new-plato-button"
            className="bg-duetto-primary text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 hover:bg-opacity-90 transition-colors text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Nuevo Plato</span>
            <span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {/* Tabs para filtrar por tipo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1.5 sm:p-2 mb-4">
        <div className="flex space-x-1 sm:space-x-2">
          <button
            onClick={() => setFiltroTipo('plato')}
            className={`flex-1 px-2 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              filtroTipo === 'plato'
                ? 'bg-duetto-primary text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="hidden sm:inline">🍽️ </span>Platos ({platos.filter(p => p.tipo === 'plato').length})
          </button>
          <button
            onClick={() => setFiltroTipo('servicio')}
            className={`flex-1 px-2 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              filtroTipo === 'servicio'
                ? 'bg-duetto-primary text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="hidden sm:inline">⚙️ </span>Servicios ({platos.filter(p => p.tipo === 'servicio').length})
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={filtroTipo === 'plato' ? 'Buscar platos...' : 'Buscar servicios...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setFiltroActivo('true')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filtroActivo === 'true'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              Activos
            </button>
            <button
              onClick={() => setFiltroActivo('false')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filtroActivo === 'false'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              Inactivos
            </button>
            <button
              onClick={() => setFiltroActivo('todos')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filtroActivo === 'todos'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlatos.map((plato) => {
          const platoAlergenos = plato.alergenos_ids || [];
          return (
            <div key={plato.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden relative">
              {/* Pestaña de Revisado */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleRevisado(plato.id, plato.revisado);
                }}
                className={`absolute top-2 right-2 z-10 px-2 py-1 text-xs font-semibold rounded-md transition-colors ${
                  plato.revisado
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white/80 text-gray-500 border border-gray-300 hover:bg-gray-100'
                }`}
                title={plato.revisado ? 'Marcar como no revisado' : 'Marcar como revisado'}
              >
                {plato.revisado ? '✓ Revisado' : '○ Sin revisar'}
              </button>
              
              {/* Indicador de Oculto en Documentos (solo visual, sin click) */}
              {plato.oculto_en_documentos && (
                <div className="absolute top-2 left-2 z-10 px-2 py-1 text-xs font-semibold rounded-md bg-orange-500 text-white">
                  Oculto en docs
                </div>
              )}
              
              <div className="h-32 bg-gradient-to-br from-duetto-secondary to-duetto-primary flex items-center justify-center">
                <ChefHat className="w-16 h-16 text-white opacity-80" />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{plato.nombre}</h3>
                  {plato.activo ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">Activo</span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">Inactivo</span>
                  )}
                </div>
                
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{plato.tiempo_preparacion_min} min</span>
                  <span className="mx-2">•</span>
                  <span>{Object.keys(plato.ingredientes || {}).length} ingredientes</span>
                </div>

                {platoAlergenos.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Alérgenos:</p>
                    <AlergenoList 
                      alergenos={platoAlergenos.map(id => alergenos.find(a => a.id === id)).filter(Boolean)}
                      size="sm"
                      maxDisplay={5}
                    />
                  </div>
                )}

                <div className="border-t border-gray-200 pt-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Precio/ración</span>
                    <span className="text-lg font-bold text-duetto-primary">
                      €{((plato.precio_sugerido || 0) / (plato.porciones || 1)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-600">Coste/ración</span>
                    <span className="text-sm text-gray-900">
                      €{((plato.coste_total || 0) / (plato.porciones || 1)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">Margen</span>
                    <span className="text-xs font-medium text-green-600">
                      {plato.coste_total > 0 
                        ? `${(((plato.precio_sugerido - plato.coste_total) / plato.coste_total) * 100).toFixed(1)}%`
                        : 'N/A'
                      }
                    </span>
                  </div>
                  {plato.porciones > 1 && (
                    <div className="text-xs text-gray-400 mt-1 text-right">
                      Total: €{plato.precio_sugerido?.toFixed(2) || '0.00'} ({plato.porciones} raciones)
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button 
                    onClick={() => openModal(plato)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center space-x-1"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => handleDelete(plato.id)}
                    className="px-3 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredPlatos.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No se encontraron platos</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {editingPlato ? 'Editar Plato' : 'Nuevo Plato'}
                </h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700 p-1">
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              
              {/* Tabs */}
              {editingPlato && (
                <div className="flex space-x-1 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setActiveTab('datos')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'datos'
                        ? 'text-duetto-primary border-b-2 border-duetto-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Datos del Plato
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('tareas')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'tareas'
                        ? 'text-duetto-primary border-b-2 border-duetto-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Tareas Operativas ({tareasPlato.length})
                  </button>
                </div>
              )}
            </div>

            {/* Tab Content: Datos del Plato */}
            {activeTab === 'datos' && (
            <form onSubmit={handleSubmit} className="p-6">
              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Información Básica</h3>
                
                {/* Tipo como toggle visual */}
                <div className="mb-4 bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo *</label>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, tipo: 'plato' })}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        formData.tipo === 'plato'
                          ? 'bg-duetto-primary text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      🍽️ Plato
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, tipo: 'servicio', categoria: null })}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        formData.tipo === 'servicio'
                          ? 'bg-duetto-primary text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      ⚙️ Servicio
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre del Plato *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre en Inglés <span className="text-gray-400 font-normal">(para minutas bilingües)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.nombre_en}
                        onChange={(e) => setFormData({ ...formData, nombre_en: e.target.value })}
                        placeholder="Traducción para minutas bilingües"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          if (!formData.nombre) {
                            alert('Primero escribe el nombre del plato en español');
                            return;
                          }
                          setTranslating(true);
                          let retries = 2;
                          while (retries > 0) {
                            try {
                              const controller = new AbortController();
                              const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
                              
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platos/traducir-nombre`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ nombre: formData.nombre }),
                                signal: controller.signal
                              });
                              clearTimeout(timeoutId);
                              
                              if (response.ok) {
                                const data = await response.json();
                                setFormData({ ...formData, nombre_en: data.nombre_en });
                                break; // Éxito, salir del loop
                              } else {
                                const errorData = await response.json().catch(() => ({}));
                                if (retries === 1) {
                                  alert(`Error al traducir: ${errorData.detail || 'Error del servidor'}`);
                                }
                              }
                            } catch (error) {
                              console.error('Error traduciendo:', error);
                              if (retries === 1) {
                                if (error.name === 'AbortError') {
                                  alert('La traducción tardó demasiado. Inténtalo de nuevo.');
                                } else {
                                  alert('Error de conexión. Verifica tu conexión a internet.');
                                }
                              }
                            }
                            retries--;
                            if (retries > 0) await new Promise(r => setTimeout(r, 1000)); // Espera 1s antes de reintentar
                          }
                          setTranslating(false);
                        }}
                        disabled={translating || !formData.nombre}
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        title="Traducir con IA"
                      >
                        {translating ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            <span className="text-xs">Traduciendo...</span>
                          </>
                        ) : (
                          <>🌐 Traducir</>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Usa el botón para traducir automáticamente con IA
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoría {formData.tipo === 'servicio' && '(automática)'}
                    </label>
                    {formData.tipo === 'servicio' ? (
                      <input
                        type="text"
                        value="Servicio"
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                    ) : (
                      <select
                        value={formData.categoria}
                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      >
                        <option value="">-- Sin categoría --</option>
                        <option value="desayuno">Desayuno</option>
                        <option value="comida">Comida</option>
                        <option value="postre">Postre</option>
                        <option value="bebida">Bebida</option>
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Raciones *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.porciones}
                      onChange={(e) => setFormData({ ...formData, porciones: e.target.value === '' ? '' : (parseInt(e.target.value) || 1) })}
                      onBlur={(e) => { if (e.target.value === '') setFormData({ ...formData, porciones: 1 }); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ¿Para cuántas raciones es esta elaboración? Ej: Tabla ibéricos para 10 personas
                    </p>
                    <label className="flex items-start cursor-pointer mt-3">
                      <input
                        type="checkbox"
                        checked={formData.elaboracion_entera || false}
                        onChange={(e) => setFormData({ ...formData, elaboracion_entera: e.target.checked })}
                        className="w-4 h-4 mt-0.5 text-duetto-primary border-gray-300 rounded focus:ring-duetto-primary"
                      />
                      <span className="ml-2 text-xs text-gray-700">
                        <span className="font-medium">Elaboraciones completas</span> — redondea hacia arriba el material <strong>fijo</strong> (p. ej. 7,1 elaboraciones → 8 manteles). Para mesa/servicio. No afecta al material "por unidad".
                      </span>
                    </label>
                  </div>
                </div>

                {/* Tiempos simplificados - autocalcula el total */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">
                    Tiempo de Preparación
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tiempo Activo (min) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.tiempo_activo_min || ''}
                        onChange={(e) => {
                          const activo = parseFloat(e.target.value) || 0;
                          const pasivo = formData.tiempo_pasivo_min || 0;
                          setFormData({ 
                            ...formData, 
                            tiempo_activo_min: activo,
                            tiempo_preparacion_min: activo + pasivo
                          });
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">Requiere atención del cocinero (acepta decimales: ej. 0.2)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tiempo Pasivo (min)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.tiempo_pasivo_min || ''}
                        onChange={(e) => {
                          const pasivo = parseFloat(e.target.value) || 0;
                          const activo = formData.tiempo_activo_min || 0;
                          setFormData({ 
                            ...formData, 
                            tiempo_pasivo_min: pasivo,
                            tiempo_preparacion_min: activo + pasivo
                          });
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">Horno/Microondas (acepta decimales: ej. 0.5)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total
                      </label>
                      <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-semibold">
                        {formData.tiempo_preparacion_min || 0} min
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Calculado automáticamente</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Precio Sugerido por Ración
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={(formData.precio_sugerido / (formData.porciones || 1)).toFixed(2)}
                      onChange={(e) => {
                        const precioPorRacion = parseFloat(e.target.value) || 0;
                        setFormData({ ...formData, precio_sugerido: precioPorRacion * (formData.porciones || 1) });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Total elaboración ({formData.porciones} raciones): €{formData.precio_sugerido?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                        className="w-4 h-4 text-duetto-primary border-gray-300 rounded focus:ring-duetto-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Plato activo</span>
                    </label>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.oculto_en_presupuestos}
                        onChange={(e) => setFormData({ ...formData, oculto_en_presupuestos: e.target.checked })}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Ocultar en presupuestos PDF</span>
                    </label>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.oculto_en_minutas}
                        onChange={(e) => setFormData({ ...formData, oculto_en_minutas: e.target.checked })}
                        className="w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Ocultar en minutas</span>
                    </label>
                  </div>
                </div>
              </div>


              {/* Instrucciones y Formato */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Elaboración y Presentación</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instrucciones de Elaboración
                    </label>
                    <textarea
                      rows="3"
                      value={formData.instrucciones_elaboracion}
                      onChange={(e) => setFormData({ ...formData, instrucciones_elaboracion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Ej: Calentar 7 min a 180°C, cortar con molde circular Ø6cm, insertar brocheta golf"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pasos de elaboración para cocina</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Formato de Presentación
                    </label>
                    <input
                      type="text"
                      value={formData.formato_presentacion}
                      onChange={(e) => setFormData({ ...formData, formato_presentacion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Ej: 5 filas x 4 = 20 uds por bandeja pizarra negra"
                    />
                    <p className="text-xs text-gray-500 mt-1">Cómo se presenta el plato en el evento</p>
                  </div>
                </div>
              </div>

              {/* CONFIGURACIÓN DE BANDEJA */}
              {formData.tipo === 'plato' && (
              <div className="mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    📦 Configuración de Bandeja
                  </h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ¿Este plato tiene bandeja asignada?
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="tiene_bandeja"
                          checked={!formData.tiene_bandeja}
                          onChange={() => setFormData({ ...formData, tiene_bandeja: false, bandeja_material_id: '', bandeja_nombre: '' })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">No</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="tiene_bandeja"
                          checked={formData.tiene_bandeja}
                          onChange={() => setFormData({ ...formData, tiene_bandeja: true })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Sí</span>
                      </label>
                    </div>
                  </div>

                  {formData.tiene_bandeja && (
                    <div className="bg-white rounded-lg p-4 border border-blue-100 space-y-4">
                      {/* Selector de bandeja con autocompletado */}
                      <div className="relative bandeja-autocomplete">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Seleccionar Bandeja
                        </label>
                        <input
                          type="text"
                          value={bandejaSearch || formData.bandeja_nombre}
                          onChange={(e) => {
                            setBandejaSearch(e.target.value);
                            setShowBandejaDropdown(true);
                            if (!e.target.value) {
                              setFormData({ ...formData, bandeja_material_id: '', bandeja_nombre: '' });
                            }
                          }}
                          onFocus={() => {
                            setBandejaSearch(formData.bandeja_nombre);
                            setShowBandejaDropdown(true);
                          }}
                          placeholder="Buscar bandeja... (ej: Bandeja Pizarra)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        
                        {/* Dropdown de bandejas */}
                        {showBandejaDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {materiales
                              .filter(m => m.nombre.toLowerCase().includes('bandeja'))
                              .filter(m => !bandejaSearch || normalizeText(m.nombre).includes(normalizeText(bandejaSearch)))
                              .slice(0, 10)
                              .map(material => (
                                <div
                                  key={material.id}
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      bandeja_material_id: material.id,
                                      bandeja_nombre: material.nombre
                                    });
                                    setBandejaSearch('');
                                    setShowBandejaDropdown(false);
                                  }}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center"
                                >
                                  <span className="text-sm text-gray-900">{material.nombre}</span>
                                  <span className="text-xs text-gray-500">{material.tipo}</span>
                                </div>
                              ))}
                            {materiales.filter(m => m.nombre.toLowerCase().includes('bandeja')).length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500 italic">
                                No hay bandejas en el catálogo de materiales
                              </div>
                            )}
                          </div>
                        )}
                        
                        {formData.bandeja_nombre && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded">
                            <span>✓ Seleccionada:</span>
                            <strong>{formData.bandeja_nombre}</strong>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, bandeja_material_id: '', bandeja_nombre: '' })}
                              className="ml-auto text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Unidades por bandeja */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unidades por bandeja
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={formData.uds_por_bandeja}
                            onChange={(e) => setFormData({ ...formData, uds_por_bandeja: e.target.value === '' ? '' : (parseInt(e.target.value) || 1) })}
                            onBlur={(e) => { if (e.target.value === '') setFormData({ ...formData, uds_por_bandeja: 1 }); }}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                          />
                          <span className="text-sm text-gray-600">raciones caben en 1 bandeja</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Esto se usará para calcular la distribución en la Orden de Cocina
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}


              {/* Ingredients Management */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ingredientes de la Receta</h3>
                <p className="text-sm text-gray-600 mb-4">Añade ingredientes y especifica las cantidades necesarias</p>
                
                {/* Add Ingredient Selector with Autocomplete */}
                <div className="mb-4 relative ingredient-autocomplete">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Añadir Ingrediente
                  </label>
                  <input
                    type="text"
                    value={ingredienteSearch}
                    onChange={(e) => {
                      setIngredienteSearch(e.target.value);
                      setShowIngredienteDropdown(true);
                    }}
                    onFocus={() => setShowIngredienteDropdown(true)}
                    placeholder="Buscar ingrediente... (ej: jamón)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  />
                  
                  {/* Dropdown de resultados */}
                  {showIngredienteDropdown && ingredienteSearch && filteredIngredientes.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredIngredientes.slice(0, 10).map(ingrediente => (
                        <div
                          key={ingrediente.id}
                          onClick={() => {
                            addIngrediente(ingrediente.id);
                            setIngredienteSearch('');
                            setShowIngredienteDropdown(false);
                          }}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                        >
                          <span className="text-sm text-gray-900">{ingrediente.nombre}</span>
                          <span className="text-xs text-gray-500">€{(ingrediente.precio_unitario || 0).toFixed(2)}/{ingrediente.unidad || 'ud'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {showIngredienteDropdown && ingredienteSearch && filteredIngredientes.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                      <p className="text-sm text-gray-500">No se encontraron ingredientes</p>
                    </div>
                  )}
                </div>

                {/* Ingredients List */}
                {Object.keys(formData.ingredientes).length > 0 ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingrediente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Coste</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(formData.ingredientes).map(([ingredienteId, cantidad]) => {
                          const ingrediente = ingredientes.find(i => i.id === ingredienteId);
                          if (!ingrediente) return null;
                          const coste = (ingrediente.precio_unitario || 0) * (cantidad || 0);
                          const inactivo = ingrediente.activo === false;
                          return (
                            <tr key={ingredienteId} className={inactivo ? 'bg-red-50' : ''}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span>{ingrediente.nombre}</span>
                                  {inactivo && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-semibold uppercase tracking-wide">
                                      Ingrediente desactivado
                                    </span>
                                  )}
                                </div>
                                {inactivo && (
                                  <button
                                    type="button"
                                    onClick={() => reactivarIngrediente(ingrediente)}
                                    className="mt-1 text-xs text-red-700 hover:text-red-900 underline"
                                  >
                                    Reactivar ingrediente
                                  </button>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  min="0.001"
                                  step="any"
                                  value={cantidad}
                                  onChange={(e) => updateIngredienteCantidad(ingredienteId, e.target.value)}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                                />
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{ingrediente.unidad || 'ud'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                €{coste.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={() => removeIngrediente(ingredienteId)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* Total de ingredientes */}
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Total Ingredientes:</span>
                        <span className="text-lg font-bold text-gray-900">
                          €{calculateCostBreakdown().ingredientes.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <p className="text-gray-500 text-sm">No hay ingredientes añadidos</p>
                    <p className="text-gray-400 text-xs mt-1">Selecciona ingredientes del desplegable superior</p>
                  </div>
                )}
              </div>


              {/* Materials Management */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">Material y Menaje</h3>
                  <button
                    type="button"
                    onClick={addMaterial}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Añadir Material
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Materiales de elaboración, presentación y desechables necesarios por persona
                </p>

                {formData.material_elaboracion.length > 0 ? (
                  <div className="space-y-3">
                    {formData.material_elaboracion.map((material, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        {/* Row 1: Material selector con autocompletado */}
                        <div className="grid grid-cols-12 gap-2 mb-2">
                          <div className="col-span-10">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Material</label>
                            <div className="relative material-autocomplete">
                              <input
                                type="text"
                                value={materialEditIndex === index ? materialSearch : (materiales.find(m => m.id === material.material_id)?.nombre || '')}
                                onChange={(e) => {
                                  setMaterialSearch(e.target.value);
                                  setMaterialEditIndex(index);
                                  setShowMaterialDropdown(true);
                                }}
                                onFocus={() => {
                                  setMaterialEditIndex(index);
                                  setMaterialSearch('');
                                  setShowMaterialDropdown(true);
                                }}
                                placeholder="Buscar material... (ej: film)"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                              />
                              
                              {/* Dropdown de materiales */}
                              {showMaterialDropdown && materialEditIndex === index && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                  {filteredMateriales.length > 0 ? (
                                    <>
                                      {filteredMateriales.slice(0, 10).map(mat => {
                                        const precio = mat.precio_unitario || 0;
                                        const categoria = mat.categoria || 'sin categoría';
                                        return (
                                          <div
                                            key={mat.id}
                                            onClick={() => {
                                              updateMaterial(index, 'material_id', mat.id);
                                              updateMaterial(index, 'nombre', mat.nombre);
                                              setShowMaterialDropdown(false);
                                              setMaterialEditIndex(null);
                                              setMaterialSearch('');
                                            }}
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                          >
                                            <div className="flex justify-between items-center">
                                              <span className="text-sm text-gray-900">{mat.nombre}</span>
                                              <span className="text-xs text-gray-500">
                                                {precio > 0 ? `€${precio.toFixed(2)}/${mat.unidad_medida}` : 'Sin coste'}
                                              </span>
                                            </div>
                                            <div className="text-xs text-gray-500">{categoria}</div>
                                          </div>
                                        );
                                      })}
                                      <div
                                        onClick={() => {
                                          setShowCreateMaterialModal(true);
                                          setShowMaterialDropdown(false);
                                        }}
                                        className="px-3 py-2 hover:bg-green-50 cursor-pointer border-t border-gray-200 text-green-600 font-semibold"
                                      >
                                        + Crear nuevo material
                                      </div>
                                    </>
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                      No se encontraron materiales
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Mostrar coste del material si existe */}
                            {material.material_id && material.cantidad && (() => {
                              const materialData = materiales.find(m => m.id === material.material_id);
                              if (materialData) {
                                const precio = materialData.precio_unitario || 0;
                                const cantidad = parseFloat(material.cantidad) || 0;
                                if (precio > 0 && cantidad > 0) {
                                  const coste = precio * cantidad;
                                  return (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Coste: €{coste.toFixed(4)} ({cantidad} × €{precio.toFixed(2)})
                                    </p>
                                  );
                                } else if (precio === 0) {
                                  return (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Sin coste (bandeja, recipiente reutilizable, etc.)
                                    </p>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </div>
                          <div className="col-span-2 flex items-end justify-center">
                            <button
                              type="button"
                              onClick={() => removeMaterial(index)}
                              className="text-red-600 hover:text-red-800 p-2"
                              title="Eliminar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Row 2: Tipo de uso */}
                        <div className="grid grid-cols-12 gap-2 mb-2">
                          <div className="col-span-6">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de uso</label>
                            <select
                              value={material.tipo_uso || 'fijo'}
                              onChange={(e) => {
                                updateMaterial(index, 'tipo_uso', e.target.value);
                                // Si cambia a fijo, resetear unidades_plato; si vuelve a por_unidades, poner 1
                                if (e.target.value === 'fijo') {
                                  updateMaterial(index, 'unidades_plato', null);
                                } else if (e.target.value === 'por_unidades' && !material.unidades_plato) {
                                  updateMaterial(index, 'unidades_plato', 1);
                                }
                              }}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                            >
                              <option value="fijo">🔧 Fijo (por elaboración)</option>
                              <option value="por_unidades">📊 Por unidades</option>
                            </select>
                          </div>
                          <div className="col-span-6">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Categoría</label>
                            <select
                              value={material.tipo || 'elaboracion'}
                              onChange={(e) => updateMaterial(index, 'tipo', e.target.value)}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                            >
                              <option value="elaboracion">Elaboración</option>
                              <option value="presentacion">Presentación</option>
                              <option value="desechable">Desechable</option>
                            </select>
                          </div>
                        </div>

                        {/* Row 3: Cantidad y ratio */}
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
                            <input
                              type="number"
                              placeholder="Ej: 1"
                              min="0"
                              step="any"
                              value={material.cantidad}
                              onChange={(e) => updateMaterial(index, 'cantidad', parseFloat(e.target.value))}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                            />
                          </div>
                          <div className="col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Unidad</label>
                            <select
                              value={material.unidad || 'ud'}
                              onChange={(e) => updateMaterial(index, 'unidad', e.target.value)}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                            >
                              <option value="ud">unidades</option>
                              <option value="hojas">hojas</option>
                              <option value="m">metros</option>
                              <option value="g">gramos</option>
                              <option value="ml">ml</option>
                            </select>
                          </div>
                          
                          {material.tipo_uso === 'por_unidades' && (
                            <div className="col-span-6">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Cada cuántas raciones
                              </label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  placeholder="Ej: 1, 20, 100"
                                  min="1"
                                  value={material.unidades_plato || 1}
                                  onChange={(e) => updateMaterial(index, 'unidades_plato', parseInt(e.target.value))}
                                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                                />
                                <span className="text-xs text-gray-500 whitespace-nowrap">raciones</span>
                              </div>
                            </div>
                          )}
                          
                          {material.tipo_uso === 'fijo' && (
                            <div className="col-span-6">
                              <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-5">
                                <p className="text-xs text-blue-700">
                                  ℹ️ Por elaboración: esta cantidad por cada elaboración del plato (escala con personas ÷ raciones)
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Ayuda visual */}
                        {material.tipo_uso === 'por_unidades' && material.unidades_plato && (
                          <div className="mt-2 bg-green-50 border border-green-200 rounded p-2">
                            <p className="text-xs text-green-700">
                              📐 <strong>Ejemplo:</strong> Para 100 platos se necesitarían{' '}
                              {Math.ceil(100 / (material.unidades_plato || 1)) * (material.cantidad || 1)}{' '}
                              {material.nombre || 'materiales'}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <p className="text-gray-500 text-sm">No hay materiales añadidos</p>
                    <p className="text-gray-400 text-xs mt-1">
                      Ej: Papel horno, brochetas, bandejas, servilletas...
                    </p>
                  </div>
                )}
              </div>


              {/* Cost Summary and Allergens */}
              {(Object.keys(formData.ingredientes).length > 0 || (formData.material_elaboracion || []).length > 0) && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        Resumen de Costes
                      </h4>
                      <div className="space-y-1">
                        {(() => {
                          const breakdown = calculateCostBreakdown();
                          return (
                            <>
                              {/* Desglose de costes */}
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>Ingredientes:</span>
                                <span>€{breakdown.ingredientes.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>Materiales:</span>
                                <span>€{breakdown.materiales.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>Personal ({formData.tiempo_activo_min || 0}min activo):</span>
                                <span>€{breakdown.personal.toFixed(2)}</span>
                              </div>
                              
                              {/* Total con línea separadora */}
                              <div className="flex justify-between text-sm border-t border-gray-300 pt-1 mt-1">
                                <span className="font-semibold text-gray-900">Coste Total:</span>
                                <span className="font-bold text-gray-900">€{breakdown.total.toFixed(2)}</span>
                              </div>
                              
                              {formData.porciones > 1 && (
                                <div className="flex justify-between text-sm bg-blue-50 px-2 py-1 rounded">
                                  <span className="font-semibold text-blue-900">Coste por Ración ({formData.porciones} raciones):</span>
                                  <span className="font-bold text-blue-900">€{(breakdown.total / formData.porciones).toFixed(2)}</span>
                                </div>
                              )}
                              
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Precio Sugerido:</span>
                                <span className="font-bold text-duetto-primary">€{formData.precio_sugerido.toFixed(2)}</span>
                              </div>
                              
                              {formData.porciones > 1 && (
                                <div className="flex justify-between text-sm bg-green-50 px-2 py-1 rounded">
                                  <span className="font-semibold text-green-900">Precio por Ración ({formData.porciones} raciones):</span>
                                  <span className="font-bold text-green-900">€{(formData.precio_sugerido / formData.porciones).toFixed(2)}</span>
                                </div>
                              )}
                              
                              <div className="flex justify-between text-sm border-t border-gray-300 pt-1 mt-1">
                                <span className="text-gray-600">Margen:</span>
                                <span className={`font-bold ${
                                  formData.precio_sugerido > breakdown.total 
                                    ? 'text-green-600' 
                                    : 'text-red-600'
                                }`}>
                                  {breakdown.total > 0 
                                    ? `${(((formData.precio_sugerido - breakdown.total) / breakdown.total) * 100).toFixed(1)}%`
                                    : 'N/A'
                                  }
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Alérgenos Heredados</h4>
                      {getInheritedAlergenos().length > 0 ? (
                        <AlergenoList 
                          alergenos={getInheritedAlergenos().map(id => alergenos.find(a => a.id === id)).filter(Boolean)}
                          size="md"
                          showLabels={true}
                        />
                      ) : (
                        <p className="text-sm text-gray-500 italic">Sin alérgenos</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    saving 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-duetto-primary hover:bg-opacity-90'
                  } text-white`}
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Guardando...' : (editingPlato ? 'Actualizar Plato' : 'Crear Plato')}
                </button>
              </div>
            </form>
            )}

            {/* Tab Content: Tareas Operativas */}
            {activeTab === 'tareas' && editingPlato && (
              <div className="p-6">
                <div className="mb-4 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Tareas Operativas del Plato</h3>
                  <button
                    type="button"
                    onClick={() => openTareaModal()}
                    className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Nueva Tarea</span>
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Define las tareas operativas estándar para este plato. Cuando el plato se incluya en un evento, estas tareas se generarán automáticamente calculando lotes y tiempos.
                </p>

                {loadingTareas ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Cargando tareas...</p>
                  </div>
                ) : tareasPlato.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-yellow-800">
                      Este plato no tiene tareas definidas. Se creará una tarea genérica cuando se incluya en un evento.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {['-2', '-1', '0', '+1'].map(fase => {
                      const tareasFase = tareasPlato.filter(t => t.fase === fase);
                      if (tareasFase.length === 0) return null;

                      const faseLabels = {
                        '-2': 'Preparación de material (–2 días)',
                        '-1': 'Preproducción / Descongelado (–1 día)',
                        '0': 'Producción del día (día 0)',
                        '+1': 'Postevento / Limpieza (+1 día)'
                      };

                      const faseColors = {
                        '-2': 'bg-blue-50 border-blue-300',
                        '-1': 'bg-orange-50 border-orange-300',
                        '0': 'bg-green-50 border-green-300',
                        '+1': 'bg-gray-50 border-gray-300'
                      };

                      return (
                        <div key={fase} className="space-y-2">
                          <h4 className={`text-sm font-semibold px-3 py-1 rounded ${faseColors[fase]}`}>
                            {faseLabels[fase]}
                          </h4>
                          {tareasFase.map(tarea => (
                            <div key={tarea.id} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h5 className="font-medium text-gray-900">{tarea.nombre_tarea}</h5>
                                  <div className="grid grid-cols-2 gap-3 mt-2 text-sm text-gray-600">
                                    <div>
                                      <span className="font-medium">Lote:</span> {tarea.lote_unidades} unidades
                                    </div>
                                    <div>
                                      <span className="font-medium">Tiempo/lote:</span> {tarea.tiempo_por_lote_min} min
                                      {tarea.tiempo_activo_min > 0 && (
                                        <span className="text-xs text-gray-400 ml-1">({tarea.tiempo_activo_min}' activo + {tarea.tiempo_pasivo_min}' pasivo)</span>
                                      )}
                                    </div>
                                    <div>
                                      <span className="font-medium">Responsable:</span> {tarea.responsable_sugerido}
                                    </div>
                                    {tarea.tipo_tiempo_pasivo === 'fijo' && tarea.capacidad_recurso > 0 && (
                                      <div>
                                        <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded text-xs">
                                          {tarea.nombre_recurso || 'Recurso'}: {tarea.capacidad_recurso} uds/tanda (tiempo fijo)
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {tarea.observaciones && (
                                    <p className="mt-2 text-sm text-gray-500 italic">{tarea.observaciones}</p>
                                  )}
                                </div>
                                <div className="flex space-x-2 ml-4">
                                  <button
                                    type="button"
                                    onClick={() => openTareaModal(tarea)}
                                    className="p-2 text-gray-500 hover:text-duetto-primary"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTarea(tarea.id)}
                                    className="p-2 text-gray-500 hover:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Crear Material Rápido */}
      {showCreateMaterialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Crear Material Rápido</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Material *
                </label>
                <input
                  type="text"
                  value={newMaterialName}
                  onChange={(e) => setNewMaterialName(e.target.value)}
                  placeholder="Ej: Papel de horno"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  value={newMaterialTipo}
                  onChange={(e) => setNewMaterialTipo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                >
                  <option value="desechable">Desechable</option>
                  <option value="menaje">Menaje de eventos</option>
                  <option value="utensilios">Utensilios de cocina</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateMaterialModal(false);
                  setNewMaterialName('');
                  setNewMaterialTipo('desechable');
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateQuickMaterial}
                className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90"
              >
                Crear y Usar
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal: Crear/Editar Tarea de Plato */}
      {showTareaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              {editingTarea ? 'Editar Tarea' : 'Nueva Tarea Operativa'}
            </h3>
            
            <form onSubmit={handleTareaSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Tarea *
                </label>
                <input
                  type="text"
                  required
                  value={tareaFormData.nombre_tarea}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, nombre_tarea: e.target.value })}
                  placeholder="Ej: Sacar bollería del congelador y pasar a frigorífico"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fase *
                </label>
                <select
                  required
                  value={tareaFormData.fase}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, fase: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                >
                  <option value="-2">Preparación de material (–2 días)</option>
                  <option value="-1">Preproducción / Descongelado (–1 día)</option>
                  <option value="0">Producción del día (día 0)</option>
                  <option value="+1">Postevento / Limpieza (+1 día)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tamaño de Lote (unidades) *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={tareaFormData.lote_unidades}
                    onChange={(e) => setTareaFormData({ ...tareaFormData, lote_unidades: e.target.value === '' ? '' : (parseInt(e.target.value) || 1) })}
                    onBlur={(e) => { if (e.target.value === '') setTareaFormData({ ...tareaFormData, lote_unidades: 1 }); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cuántas unidades se hacen a la vez</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tiempo por Lote (minutos) *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={tareaFormData.tiempo_por_lote_min}
                    onChange={(e) => setTareaFormData({ ...tareaFormData, tiempo_por_lote_min: e.target.value === '' ? '' : (parseInt(e.target.value) || 1) })}
                    onBlur={(e) => { if (e.target.value === '') setTareaFormData({ ...tareaFormData, tiempo_por_lote_min: 1 }); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minutos totales (activo + pasivo) por lote</p>
                </div>
              </div>

              {/* Desglose activo/pasivo y capacidad de recurso */}
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Desglose de tiempos y equipamiento</span>
                  <span className="text-xs text-amber-500">(opcional - para cálculos precisos)</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tiempo activo (min)</label>
                    <input
                      type="number"
                      min="0"
                      value={tareaFormData.tiempo_activo_min || ''}
                      onChange={(e) => setTareaFormData({ ...tareaFormData, tiempo_activo_min: parseInt(e.target.value) || 0 })}
                      placeholder="Ej: 5"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      data-testid="tarea-tiempo-activo"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">Manos de trabajo por lote</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tiempo pasivo (min)</label>
                    <input
                      type="number"
                      min="0"
                      value={tareaFormData.tiempo_pasivo_min || ''}
                      onChange={(e) => setTareaFormData({ ...tareaFormData, tiempo_pasivo_min: parseInt(e.target.value) || 0 })}
                      placeholder="Ej: 15"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      data-testid="tarea-tiempo-pasivo"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">Horno, microondas, cafetera...</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de tiempo pasivo</label>
                  <div className="flex gap-4">
                    <label className={`flex-1 flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${
                      tareaFormData.tipo_tiempo_pasivo === 'proporcional' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="tipo_pasivo"
                        checked={tareaFormData.tipo_tiempo_pasivo === 'proporcional'}
                        onChange={() => setTareaFormData({ ...tareaFormData, tipo_tiempo_pasivo: 'proporcional' })}
                        className="text-blue-600"
                        data-testid="tarea-pasivo-proporcional"
                      />
                      <div>
                        <span className="font-medium">Proporcional</span>
                        <p className="text-[10px] text-gray-400">Escala con cada lote (reposo, enfriado)</p>
                      </div>
                    </label>
                    <label className={`flex-1 flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${
                      tareaFormData.tipo_tiempo_pasivo === 'fijo' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="tipo_pasivo"
                        checked={tareaFormData.tipo_tiempo_pasivo === 'fijo'}
                        onChange={() => setTareaFormData({ ...tareaFormData, tipo_tiempo_pasivo: 'fijo' })}
                        className="text-amber-600"
                        data-testid="tarea-pasivo-fijo"
                      />
                      <div>
                        <span className="font-medium">Fijo por tanda</span>
                        <p className="text-[10px] text-gray-400">Horno, microondas, cafetera</p>
                      </div>
                    </label>
                  </div>
                </div>

                {tareaFormData.tipo_tiempo_pasivo === 'fijo' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Recurso / Equipo</label>
                      <input
                        type="text"
                        value={tareaFormData.nombre_recurso}
                        onChange={(e) => setTareaFormData({ ...tareaFormData, nombre_recurso: e.target.value })}
                        placeholder="Ej: Horno, Cafetera, Microondas"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        data-testid="tarea-nombre-recurso"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Capacidad máx. (uds/tanda)</label>
                      <input
                        type="number"
                        min="1"
                        value={tareaFormData.capacidad_recurso || ''}
                        onChange={(e) => setTareaFormData({ ...tareaFormData, capacidad_recurso: parseInt(e.target.value) || '' })}
                        placeholder="Ej: 90"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        data-testid="tarea-capacidad-recurso"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">Máximo de uds que el equipo procesa a la vez</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable Sugerido *
                </label>
                <select
                  required
                  value={tareaFormData.responsable_sugerido}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, responsable_sugerido: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                >
                  <option value="Cocina">Cocina</option>
                  <option value="Ayudante">Ayudante</option>
                  <option value="Jefe cocina">Jefe cocina</option>
                  <option value="Logística">Logística</option>
                  <option value="Montaje">Montaje</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones
                </label>
                <textarea
                  value={tareaFormData.observaciones}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, observaciones: e.target.value })}
                  placeholder="Ej: usar bandeja con papel de horno y filmar"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                {(() => {
                  const uds = 120;
                  const lote = parseInt(tareaFormData.lote_unidades) || 10;
                  const tLote = parseInt(tareaFormData.tiempo_por_lote_min) || 10;
                  const tActivo = parseInt(tareaFormData.tiempo_activo_min) || 0;
                  const tPasivo = parseInt(tareaFormData.tiempo_pasivo_min) || 0;
                  const capacidad = parseInt(tareaFormData.capacidad_recurso) || 0;
                  const numLotes = Math.ceil(uds / lote);
                  
                  if (tareaFormData.tipo_tiempo_pasivo === 'fijo' && capacidad > 0 && tActivo > 0) {
                    const tandas = Math.ceil(uds / capacidad);
                    const tiempoActTotal = numLotes * tActivo;
                    const tiempoPasTotal = tandas * tPasivo;
                    const total = tiempoActTotal + tiempoPasTotal;
                    return (
                      <p className="text-sm text-blue-900">
                        <strong>Ejemplo con {uds} personas:</strong><br />
                        {numLotes} lotes x {tActivo} min activo = <strong>{tiempoActTotal} min</strong> de trabajo<br />
                        {tandas} tanda{tandas > 1 ? 's' : ''} de {tareaFormData.nombre_recurso || 'equipo'} ({capacidad} uds/tanda) x {tPasivo} min = <strong>{tiempoPasTotal} min</strong> pasivo<br />
                        <strong>Total: {total} min</strong> (vs {numLotes * tLote} min si se multiplicase todo x lotes)
                      </p>
                    );
                  }
                  return (
                    <p className="text-sm text-blue-900">
                      <strong>Ejemplo con {uds} personas:</strong><br />
                      {numLotes} lotes x {tLote} min/lote = <strong>{numLotes * tLote} minutos</strong>
                    </p>
                  );
                })()}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeTareaModal}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90"
                >
                  {editingTarea ? 'Actualizar Tarea' : 'Crear Tarea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Desactivación con Menús Afectados */}
      {showDesactivarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="desactivar-plato-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
              <h3 className="text-lg font-bold text-amber-900">Desactivar plato</h3>
              <p className="text-sm text-amber-700 mt-1">
                <strong>"{desactivarPlatoNombre}"</strong> está en {menusAfectados.length} menú{menusAfectados.length !== 1 ? 's' : ''} activo{menusAfectados.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              {/* Lista de menús afectados */}
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                {menusAfectados.map(m => (
                  <div key={m.id} className="flex items-center gap-2 py-1 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                    {m.nombre}
                  </div>
                ))}
              </div>

              {/* Opciones */}
              <div className="space-y-3">
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    desactivarAccion === 'sustituir' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="opcion-sustituir"
                >
                  <input
                    type="radio"
                    name="accion_desactivar"
                    value="sustituir"
                    checked={desactivarAccion === 'sustituir'}
                    onChange={() => setDesactivarAccion('sustituir')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-semibold text-gray-900">Sustituir por otro plato</span>
                    <p className="text-xs text-gray-500 mt-0.5">Reemplaza este plato en todos los menús afectados</p>
                  </div>
                </label>
                
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    desactivarAccion === 'eliminar' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="opcion-eliminar"
                >
                  <input
                    type="radio"
                    name="accion_desactivar"
                    value="eliminar"
                    checked={desactivarAccion === 'eliminar'}
                    onChange={() => setDesactivarAccion('eliminar')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-semibold text-gray-900">Eliminar de los menús</span>
                    <p className="text-xs text-gray-500 mt-0.5">Quita este plato de todos los menús afectados</p>
                  </div>
                </label>
              </div>

              {/* Selector de plato sustituto */}
              {desactivarAccion === 'sustituir' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Seleccionar plato sustituto:</label>
                  <input
                    type="text"
                    value={sustitutoSearch}
                    onChange={(e) => setSustitutoSearch(e.target.value)}
                    placeholder="Buscar plato..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="buscar-sustituto-input"
                  />
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    {platos
                      .filter(p => p.activo && p.id !== desactivarPlatoId && 
                        (!sustitutoSearch || normalizeText(p.nombre).includes(normalizeText(sustitutoSearch))))
                      .slice(0, 20)
                      .map(p => (
                        <div
                          key={p.id}
                          onClick={() => { setPlatoSustitutoId(p.id); setSustitutoSearch(p.nombre); }}
                          className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 ${
                            platoSustitutoId === p.id ? 'bg-blue-100 font-semibold' : ''
                          }`}
                          data-testid={`sustituto-option-${p.id}`}
                        >
                          <span>{p.nombre}</span>
                          <span className="text-xs text-gray-400">{(p.coste || 0).toFixed(2)}/ud</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t flex gap-3 justify-end">
              <button
                onClick={() => { setShowDesactivarModal(false); setFormData({ ...formData, activo: true }); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                data-testid="cancelar-desactivar-btn"
              >
                Cancelar
              </button>
              <button
                onClick={handleDesactivarConAccion}
                disabled={procesandoDesactivar || (desactivarAccion === 'sustituir' && !platoSustitutoId)}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="confirmar-desactivar-btn"
              >
                {procesandoDesactivar && <Loader2 className="w-4 h-4 animate-spin" />}
                {desactivarAccion === 'sustituir' ? 'Sustituir y desactivar' : 'Eliminar y desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Platos;
